import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex sm:items-center sm:justify-center min-h-screen sm:px-4 sm:pt-4 sm:pb-20 text-center" }
const _hoisted_2 = { class: "inline-block align-bottom sm:align-middle bg-white dark:bg-gray-800 sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all min-h-screen w-full fixed inset-x-0 top-0 sm:static sm:min-h-0 sm:max-w-2xl sm:w-full flex flex-col" }
const _hoisted_3 = { class: "bg-gradient-to-tr from-primary to-secondary px-6 py-5 flex items-center justify-between" }
const _hoisted_4 = { class: "flex items-center gap-3 min-w-0" }
const _hoisted_5 = { class: "h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0" }
const _hoisted_6 = { class: "min-w-0" }
const _hoisted_7 = { class: "text-lg font-bold text-white truncate" }
const _hoisted_8 = { class: "text-sm text-white/70 truncate" }
const _hoisted_9 = { class: "px-4 py-5 sm:p-6 max-h-[calc(100vh-16rem)] overflow-y-auto" }
const _hoisted_10 = { class: "bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600 mb-5" }
const _hoisted_11 = { class: "text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2" }
const _hoisted_12 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }
const _hoisted_13 = { class: "md:col-span-2" }
const _hoisted_14 = { class: "text-sm font-medium text-gray-800 dark:text-gray-200 mt-1" }
const _hoisted_15 = { class: "text-sm font-medium text-gray-800 dark:text-gray-200 mt-1" }
const _hoisted_16 = { class: "text-sm font-medium text-gray-800 dark:text-gray-200 mt-1" }
const _hoisted_17 = { class: "bg-gray-50 dark:bg-gray-700/30 p-5 rounded-xl border border-gray-200 dark:border-gray-600 mb-5" }
const _hoisted_18 = { class: "text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-200 dark:border-gray-600 pb-2" }
const _hoisted_19 = { key: 0 }
const _hoisted_20 = {
  key: 0,
  class: "flex items-center justify-center bg-white dark:bg-gray-800 rounded-lg p-2 border border-gray-200 dark:border-gray-600"
}
const _hoisted_21 = ["src"]
const _hoisted_22 = {
  key: 1,
  class: "rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600"
}
const _hoisted_23 = ["src"]
const _hoisted_24 = {
  key: 2,
  class: "text-center py-8 text-gray-500 border border-dashed rounded-xl"
}
const _hoisted_25 = {
  key: 1,
  class: "text-sm text-gray-400 italic"
}
const _hoisted_26 = { class: "bg-gray-50 dark:bg-gray-700 px-4 py-3 flex flex-col gap-2 sm:px-6 sm:flex-row-reverse sm:gap-0 shrink-0" }

import { computed } from 'vue'
import { FileText, X, BookCopy, FolderOpen, CloudDownload as DownloadIcon, Printer } from 'lucide-vue-next'
import { employeeDocumentService, type EmployeeDocument } from '@/services/employeeDocumentService'
import { useToast } from 'vue-toastification'

const toastTimeout = 5000


export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentDetailsModal',
  props: {
    isOpen: { type: Boolean },
    document: {}
  },
  emits: ["close"],
  setup(__props: any) {

const toast = useToast()
const props = __props



const uploadedBy = computed(() => {
  if (!props.document?.user) return ''
  const f = props.document.user.details?.first_name || ''
  const l = props.document.user.details?.last_name || ''
  if (f && l) return `${f} ${l}`
  return props.document.user.email || ''
})

const fileUrl = computed(() => {
  if (!props.document?.file_path) return ''
  const baseUrl = (process.env.VUE_APP_API_URL || '').replace(/\/api\/?$/, '')
  return baseUrl + props.document.file_path
})

const isPreviewableImage = computed(() => {
  const ext = (props.document?.file_path || '').toLowerCase().split('.').pop()
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext || '')
})

const isPreviewablePdf = computed(() => {
  return (props.document?.file_path || '').toLowerCase().endsWith('.pdf')
})

const handlePrint = async () => {
  if (!props.document?.id) return
  try {
    await employeeDocumentService.printDocument(props.document.id)
    toast.success('Document sent to printer', { timeout: toastTimeout })
  } catch (err: any) {
    if (!err.message?.includes('opened in a new tab')) {
      toast.error(err.message || 'Failed to print document', { timeout: toastTimeout })
    }
  }
}

const handleDownload = async () => {
  if (!props.document?.id) return
  try {
    const token = localStorage.getItem('token')
    const url = employeeDocumentService.getDownloadUrl(props.document.id)
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('Invalid File')
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = props.document.title || 'download'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(downloadUrl)
    document.body.removeChild(a)
    toast.success('Document downloaded successfully', { timeout: toastTimeout })
  } catch (err: any) {
    toast.error(err.message || 'Failed to download file', { timeout: toastTimeout })
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "modal" }, {
    default: _withCtx(() => [
      (__props.isOpen)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "fixed inset-0 z-50 overflow-y-auto",
            onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (_ctx.$emit('close')), ["self"]))
          }, [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("div", {
                class: "fixed inset-0 transition-opacity bg-black bg-opacity-50 dark:bg-opacity-50",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close')))
              }),
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("div", _hoisted_5, [
                      _createVNode(_unref(FileText), { class: "h-5 w-5 text-white" })
                    ]),
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("h3", _hoisted_7, _toDisplayString(__props.document?.title || 'Document Details'), 1),
                      _createElementVNode("p", _hoisted_8, _toDisplayString(__props.document?.status), 1)
                    ])
                  ]),
                  _createElementVNode("button", {
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('close'))),
                    class: "shrink-0 p-1.5 rounded-lg hover:bg-white/10 text-white/80 hover:text-white transition-colors"
                  }, [
                    _createVNode(_unref(X), { class: "h-5 w-5" })
                  ])
                ]),
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("div", _hoisted_10, [
                    _createElementVNode("h4", _hoisted_11, [
                      _createVNode(_unref(BookCopy), { class: "h-4 w-4 inline" }),
                      _cache[4] || (_cache[4] = _createTextVNode(" Document Information ", -1))
                    ]),
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("div", _hoisted_13, [
                        _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide" }, "Title", -1)),
                        _createElementVNode("p", _hoisted_14, _toDisplayString(__props.document?.title || '—'), 1)
                      ]),
                      _createElementVNode("div", null, [
                        _cache[6] || (_cache[6] = _createElementVNode("p", { class: "text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide" }, "Status", -1)),
                        _createElementVNode("span", {
                          class: _normalizeClass(["inline-flex items-center mt-1.5 px-2.5 py-0.5 text-xs font-semibold rounded-full", __props.document?.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'])
                        }, _toDisplayString(__props.document?.status === 'active' ? 'Active' : 'Inactive'), 3)
                      ]),
                      _createElementVNode("div", null, [
                        _cache[7] || (_cache[7] = _createElementVNode("p", { class: "text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide" }, "Uploaded By", -1)),
                        _createElementVNode("p", _hoisted_15, _toDisplayString(uploadedBy.value || '—'), 1)
                      ]),
                      _createElementVNode("div", null, [
                        _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide" }, "Date Uploaded", -1)),
                        _createElementVNode("p", _hoisted_16, _toDisplayString(__props.document?.created_at ? new Date(__props.document.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'), 1)
                      ])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_17, [
                    _createElementVNode("h4", _hoisted_18, [
                      _createVNode(_unref(FolderOpen), { class: "h-4 w-4 inline" }),
                      _cache[9] || (_cache[9] = _createTextVNode(" File Preview ", -1))
                    ]),
                    (__props.document?.file_path)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                          (isPreviewableImage.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                _createElementVNode("img", {
                                  src: fileUrl.value,
                                  alt: "file preview",
                                  class: "max-w-full max-h-[50vh] rounded object-contain"
                                }, null, 8, _hoisted_21)
                              ]))
                            : (isPreviewablePdf.value)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                                  _createElementVNode("iframe", {
                                    src: fileUrl.value,
                                    class: "w-full h-[50vh]"
                                  }, null, 8, _hoisted_23)
                                ]))
                              : (_openBlock(), _createElementBlock("div", _hoisted_24, [
                                  _createVNode(_unref(FileText), { class: "h-12 w-12 mx-auto mb-2 text-gray-300 dark:text-gray-600" }),
                                  _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-base font-medium" }, "Preview not available", -1)),
                                  _cache[11] || (_cache[11] = _createElementVNode("p", { class: "text-sm mt-1" }, "This file type cannot be previewed in the browser.", -1))
                                ]))
                        ]))
                      : (_openBlock(), _createElementBlock("p", _hoisted_25, "No file attached."))
                  ])
                ]),
                _createElementVNode("div", _hoisted_26, [
                  (__props.document?.file_path)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        onClick: handlePrint,
                        class: "w-full sm:w-auto sm:ml-3 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 shadow-sm transition-all inline-flex items-center justify-center gap-2"
                      }, [
                        _createVNode(_unref(Printer), { class: "h-4 w-4" }),
                        _cache[12] || (_cache[12] = _createTextVNode(" Print ", -1))
                      ]))
                    : _createCommentVNode("", true),
                  (__props.document?.file_path)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        onClick: handleDownload,
                        class: "w-full sm:w-auto sm:ml-3 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm transition-all inline-flex items-center justify-center gap-2"
                      }, [
                        _createVNode(_unref(DownloadIcon), { class: "h-4 w-4" }),
                        _cache[13] || (_cache[13] = _createTextVNode(" Download ", -1))
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("button", {
                    type: "button",
                    class: "w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('close')))
                  }, " Close ")
                ])
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})